import React from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge, type StatusTone } from 'components/ui/status-badge';
import { formatDateLong, formatSom } from './billingHelpers';

/** Subscription status → badge tone and label. */
const STATUS_META: Record<string, { tone: StatusTone; key: string; label: string }> = {
  active: { tone: 'success', key: 'status_active', label: 'Active' },
  trialing: { tone: 'info', key: 'status_trial', label: 'Trial' },
  past_due: { tone: 'error', key: 'status_past_due', label: 'Past due' },
  unpaid: { tone: 'error', key: 'status_unpaid', label: 'Unpaid' },
  canceled: { tone: 'neutral', key: 'status_canceled', label: 'Canceled' },
  none: { tone: 'neutral', key: 'status_free_tier', label: 'Free tier' },
};

interface BillingHeroProps {
  planName: string;
  status: string;
  periodEnd?: string;
  /** What the next renewal costs (so'm), or null when nothing renews. */
  renewalPrice: number | null;
  /** Open the coin and payment history. */
  onViewHistory: () => void;
}

/** The current plan and its status on the left; the next payment and the history link on the right. */
export function BillingHero({ planName, status, periodEnd, renewalPrice, onViewHistory }: BillingHeroProps) {
  const { t } = useTranslation();
  const meta = STATUS_META[status] ?? STATUS_META.none;

  return (
    <section className="flex flex-col gap-3.5 rounded-lg border border-border bg-card px-5 py-[18px] md:flex-row md:items-center md:justify-between md:gap-6">
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="text-[12.5px] leading-4 text-muted-foreground">{t('current_plan_2', 'Current plan')}</span>
        <div className="flex min-w-0 items-center gap-2.5">
          <h3 className="truncate text-[26px] font-bold leading-8 tracking-[-0.02em] text-foreground">{planName}</h3>
          <StatusBadge tone={meta.tone} className="shrink-0">
            {t(meta.key, meta.label)}
          </StatusBadge>
        </div>
        <p className="text-[13.5px] leading-[18px] text-foreground/80">
          {status === 'none'
            ? t('no_active_subscription', 'No active subscription')
            : t('subscription_active_until', { defaultValue: 'Active until {{date}}', date: formatDateLong(periodEnd) })}
        </p>
      </div>

      <div className="flex shrink-0 flex-col items-start gap-1 md:items-end">
        <span className="text-[12.5px] leading-4 text-muted-foreground">{t('billing_next_payment', 'Next payment')}</span>
        {renewalPrice != null ? (
          <span className="text-lg font-bold leading-6 tabular-nums text-foreground">{formatSom(renewalPrice)}</span>
        ) : (
          <span className="text-[13.5px] leading-6 text-foreground/80">{t('no_renewal_scheduled', 'No renewal scheduled')}</span>
        )}
        <button
          type="button"
          onClick={onViewHistory}
          className="rounded-sm text-[13px] font-medium leading-4 text-foreground/80 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('view_history', 'View history')}
        </button>
      </div>
    </section>
  );
}

export default BillingHero;
